import React, { useState, useEffect } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import { analyzeSalesEstimate, getSalesEstimateTokens } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
    listSalesEstimateBaskets,
    createSalesEstimateBasket,
    updateSalesEstimateBasket,
    deleteSalesEstimateBasket
} from '../../services/salesEstimateService';
// --- Basket management state ---
const COUNTRIES = ['US', 'MX', 'CA', 'UK', 'DE', 'ES', 'FR', 'IT'];

// ─── Marketplace → Amazon domain mapping ───
const MARKETPLACE_DOMAINS = {
    us: 'amazon.com',
    mx: 'amazon.com.mx',
    ca: 'amazon.ca',
    uk: 'amazon.co.uk',
    de: 'amazon.de',
    fr: 'amazon.fr',
    it: 'amazon.it',
    es: 'amazon.es',
};



// ─── Color palette matching tracking2 ───
const BRAND_COLORS = [
    '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16',
];

const EMPTY_RESULT = {
    weekly: [],
    highlights: null,
    failures: [],
    dateRange: { startDate: '-', endDate: '-' },
    tokensUsed: 0,
    tokensRemaining: 0,
};

function SalesEstimate() {
        // Baskets
        const [baskets, setBaskets] = useState([]);
        const [selectedBasket, setSelectedBasket] = useState(null);
        const [showNew, setShowNew] = useState(false);
        const [showEdit, setShowEdit] = useState(false);
        const [newName, setNewName] = useState('');
        const [newAsins, setNewAsins] = useState('');
        const [newCountry, setNewCountry] = useState('US');
        const [editing, setEditing] = useState(null);
        const [editName, setEditName] = useState('');
        const [editAsins, setEditAsins] = useState('');
        const [editCountry, setEditCountry] = useState('US');
        const accountId = localStorage.getItem('selectedAccountValue') || '';

        // Load baskets on mount
        useEffect(() => {
            (async () => {
                try {
                    const res = await listSalesEstimateBaskets(accountId);
                    setBaskets(res.data || []);
                } catch (err) {}
            })();
        }, [accountId]);

        // Select basket and parse ASINs
        const handleSelectBasket = (basket) => {
            setSelectedBasket(basket);
            setFile(null);
            let asinsArr = [];
            if (Array.isArray(basket.asins)) {
                asinsArr = basket.asins;
            } else if (typeof basket.asins === 'string') {
                // Intentar parsear como JSON array
                try {
                    const parsed = JSON.parse(basket.asins);
                    if (Array.isArray(parsed)) {
                        asinsArr = parsed;
                    } else {
                        asinsArr = basket.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                    }
                } catch {
                    asinsArr = basket.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                }
            }
            // Unir líneas tipo ASIN\nBrand en ASIN,Brand
            const merged = [];
            for (let i = 0; i < asinsArr.length; i++) {
                const curr = asinsArr[i];
                if (typeof curr === 'object' && curr.asin) {
                    merged.push(curr);
                } else if (typeof curr === 'string') {
                    if (curr.includes(',')) {
                        merged.push(curr);
                    } else if (i + 1 < asinsArr.length && typeof asinsArr[i + 1] === 'string' && !asinsArr[i + 1].includes(',')) {
                        merged.push(curr + ',' + asinsArr[i + 1]);
                        i++;
                    } else {
                        merged.push(curr);
                    }
                }
            }
            // Convertir a objetos {asin, brand}
            const parsedAsins = merged.map(a => {
                if (typeof a === 'object' && a.asin) {
                    return { asin: a.asin, brand: a.brand || 'Unknown' };
                }
                if (typeof a === 'string') {
                    const [asin, ...brandParts] = a.split(',');
                    const brand = brandParts.join(',').trim();
                    return { asin: (asin || '').trim(), brand: brand || 'Unknown' };
                }
                return { asin: '', brand: 'Unknown' };
            }).filter(a => a.asin.length >= 10);
            setParsedAsins(parsedAsins);
        };

        // Create basket
        const handleCreate = async (e) => {
            e.preventDefault();
            const asins = newAsins.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
            if (!newName || asins.length === 0) {
                setError('You must enter a name and at least one ASIN.');
                return;
            }
            setLoading(true);
            setError('');
            try {
                const resp = await createSalesEstimateBasket({ name: newName, asins, country: newCountry, account_id: accountId });
                setNewName(''); setNewAsins(''); setShowNew(false);
                const res = await listSalesEstimateBaskets(accountId);
                setBaskets(res.data || []);
            } catch (err) {
                setError('Error creating basket: ' + (err.message || JSON.stringify(err)));
            } finally {
                setLoading(false);
            }
        };

        // Edit basket
        const openEdit = (basket) => {
            setEditing(basket);
            setEditName(basket.name || '');
            setEditCountry(basket.country || 'US');
            let asinsArr = [];
            if (Array.isArray(basket.asins)) {
                asinsArr = basket.asins;
            } else if (typeof basket.asins === 'string') {
                try {
                    const parsed = JSON.parse(basket.asins);
                    if (Array.isArray(parsed)) {
                        asinsArr = parsed;
                    } else {
                        asinsArr = basket.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                    }
                } catch {
                    asinsArr = basket.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                }
            }
            // Unir líneas sueltas tipo ASIN\nBrand en ASIN,Brand
            const merged = [];
            for (let i = 0; i < asinsArr.length; i++) {
                const curr = asinsArr[i];
                if (typeof curr === 'object' && curr.asin) {
                    merged.push(`${curr.asin},${curr.brand || ''}`);
                } else if (typeof curr === 'string') {
                    if (curr.includes(',')) {
                        merged.push(curr);
                    } else if (i + 1 < asinsArr.length && typeof asinsArr[i + 1] === 'string' && !asinsArr[i + 1].includes(',')) {
                        merged.push(`${curr},${asinsArr[i + 1]}`);
                        i++; // skip next
                    } else {
                        merged.push(curr);
                    }
                }
            }
            setEditAsins(merged.join('\n'));
            setShowEdit(true);
        };
        const handleEdit = async (e) => {
            e.preventDefault();
            if (!editing) {
                setError('No basket selected for editing.');
                return;
            }
            const asins = editAsins.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
            setLoading(true);
            setError('');
            try {
                const resp = await updateSalesEstimateBasket(editing.id, { name: editName, asins, country: editCountry });
                setShowEdit(false); setEditing(null);
                const res = await listSalesEstimateBaskets(accountId);
                setBaskets(res.data || []);
            } catch (err) {
                setError('Error editing basket: ' + (err.message || JSON.stringify(err)));
            } finally {
                setLoading(false);
            }
        };
        // Delete basket
        const handleDelete = async (id) => {
            if (!window.confirm('Delete this basket?')) return;
            setLoading(true);
            setError('');
            try {
                await deleteSalesEstimateBasket(id);
                const res = await listSalesEstimateBaskets(accountId);
                setBaskets(res.data || []);
                setSelectedBasket(null);
                setParsedAsins([]);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
    const [file, setFile] = useState(null);
    const [marketplace, setMarketplace] = useState('us');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [tokenInfo, setTokenInfo] = useState(null);
    const [metricMode, setMetricMode] = useState('sales_amount');
    const [parsedAsins, setParsedAsins] = useState([]);
    const safeResult = result ?? EMPTY_RESULT;

    // Load token info on mount
    useEffect(() => {
        (async () => {
            try {
                const res = await getSalesEstimateTokens();
                if (res.success) setTokenInfo(res.data);
            } catch (e) {
                console.warn('Could not load token info', e);
            }
        })();
    }, []);

    // Parse file when selected
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) {
            setFile(null);
            setParsedAsins([]);
            return;
        }
        setFile(selectedFile);

        const reader = new FileReader();
        reader.onload = (evt) => {
            const text = evt.target.result;
            let lines = text.split(/\r?\n/).map(line => line.trim()).filter(line => line && !line.startsWith('#'));
            // Ignorar encabezado si existe
            if (lines.length && /^asin[,;\t ]*brand/i.test(lines[0])) {
                lines = lines.slice(1);
            }
            // Unir líneas tipo ASIN\nBrand en ASIN,Brand
            const merged = [];
            for (let i = 0; i < lines.length; i++) {
                const curr = lines[i];
                if (curr.includes(',')) {
                    merged.push(curr);
                } else if (i + 1 < lines.length && !lines[i + 1].includes(',')) {
                    merged.push(`${curr},${lines[i + 1]}`);
                    i++;
                } else {
                    merged.push(curr);
                }
            }
            // Parsear ASIN,Brand
            const asins = merged.map(line => {
                const [asin, ...brandParts] = line.split(',');
                const brand = brandParts.join(',').trim();
                return { asin: (asin || '').trim(), brand: brand || 'Unknown' };
            }).filter(a => a.asin.length >= 10);

            // Validar formato
            const invalidLines = merged.filter(line => {
                const [asin] = line.split(',');
                return !asin || asin.length < 10;
            });
            if (invalidLines.length > 0) {
                setError('Some lines do not match the ASIN,Brand format or contain an invalid ASIN. Example: B07PXGQC1Q,Coop Home Goods');
            } else {
                setError(null);
            }
            setParsedAsins(asins);
        };
        reader.readAsText(selectedFile);
    };

    const handleSubmit = async () => {
        if (!parsedAsins.length) {
            setError('File contains no valid ASINs (minimum 10 characters per ASIN)');
            return;
        }
        if (parsedAsins.length > 10) {
            setError(`Maximum 10 ASINs per request. The file contains ${parsedAsins.length}.`);
            return;
        }

        setLoading(true);
        setError(null);
        setResult(EMPTY_RESULT);

        try {
            const res = await analyzeSalesEstimate(parsedAsins, marketplace);
            if (res.success) {
                setResult(res.data);
                setTokenInfo({
                    used: (tokenInfo.limit || 1000) - res.data.tokensRemaining,
                    limit: tokenInfo.limit || 1000,
                    remaining: res.data.tokensRemaining,
                });
            } else {
                setError(res.message);
            }
        } catch (e) {
            setError(e.response.data.message || e.message);
        } finally {
            setLoading(false);
        }
    };

    // ─── Token badge color ───
    const tokenBadgeClass = () => {
        if (!tokenInfo) return 'bg-secondary';
        const pct = tokenInfo.used / tokenInfo.limit;
        if (pct >= 0.9) return 'bg-danger';
        if (pct >= 0.7) return 'bg-warning text-dark';
        return 'bg-success';
    };

    // ─── Build overview chart data ───
    const buildOverviewData = () => {
        if (!safeResult.weekly.length) return null;

        const metricLabel = metricMode === 'sales_amount' ? 'Sales Amount (USD)' : 'Units Sold';
        const hoverFmt = metricMode === 'sales_amount' ? '$%{y : ,.0f}' : '%{y:,}';

        const brandData = {};
        for (const row of safeResult.weekly) {
            if (!brandData[row.brand]) brandData[row.brand] = [];
            brandData[row.brand].push(row);
        }

        const traces = Object.entries(brandData).map(([brand, rows], i) => {
            const sorted = [...rows].sort((a, b) => a.week_end.localeCompare(b.week_end));
            const weekAgg = {};
            for (const r of sorted) {
                if (!weekAgg[r.week_end]) weekAgg[r.week_end] = 0;
                weekAgg[r.week_end] += r[metricMode];
            }

            return {
                x: Object.keys(weekAgg),
                y: Object.values(weekAgg),
                type: 'scatter',
                mode: 'lines+markers',
                name: brand,
                line: { color: BRAND_COLORS[i % BRAND_COLORS.length] },
                hovertemplate: `Brand: ${brand}<br>Value: ${hoverFmt}<br>Week end: %{x}<extra></extra>`,
            };
        });

        return {
            traces,
            layout: {
                title: `📈 Overview — Weekly ${metricLabel} by Brand (Fri→Thu)`,
                height: 450,
                hovermode: 'x unified',
                xaxis: { title: { text: 'Week Ending (Thursday)' }, type: 'date' },
                yaxis: { title: { text: metricLabel } },
                margin: { l: 60, r: 20, t: 50, b: 55 },
                legend: { title: { text: 'Brand' } },
            },
        };
    };

    // ─── Build breakdown (small multiples) ───
    const buildBreakdownData = () => {
        if (!safeResult.weekly.length) return null;

        const brands = [...new Set(safeResult.weekly.map(r => r.brand))];
        const metricLabel = metricMode === 'sales_amount' ? 'Sales (USD)' : 'Units';
        const hoverFmt = metricMode === 'sales_amount' ? '$%{y : ,.0f}' : '%{y:,}';

        return brands.map((brand, i) => {
            const rows = safeResult.weekly.filter(r => r.brand === brand);
            const weekAgg = {};
            for (const r of rows) {
                if (!weekAgg[r.week_end]) weekAgg[r.week_end] = 0;
                weekAgg[r.week_end] += r[metricMode];
            }
            const sortedWeeks = Object.keys(weekAgg).sort();

            return {
                brand,
                trace: {
                    x: sortedWeeks,
                    y: sortedWeeks.map(w => weekAgg[w]),
                    type: 'scatter',
                    mode: 'lines+markers',
                    name: brand,
                    line: { color: BRAND_COLORS[i % BRAND_COLORS.length] },
                    hovertemplate: `${brand}<br>${metricLabel}: ${hoverFmt}<br>%{x}<extra></extra>`,
                },
            };
        });
    };

    // ─── Highlights ───
    const renderHighlights = () => {
        if (!safeResult.highlights) return null;
        const { sales, variation, units } = safeResult.highlights;

        return (
            <div className="row mb-4 g-3">
                <div className="col-md-4">
                    <div className="card h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)' }}>
                        <div className="card-body text-white">
                            <h6 className="card-title opacity-75">🏆 Sales</h6>
                            <div className="mb-2">
                                <small className="opacity-75">Highest:</small>
                                <div className="fw-bold">{sales.highest.brand} — ${sales.highest.value.toLocaleString()}</div>
                            </div>
                            <div className="mb-2">
                                <small className="opacity-75">Lowest:</small>
                                <div>{sales.lowest.brand} — ${sales.lowest.value.toLocaleString()}</div>
                            </div>
                            <div>
                                <small className="opacity-75">Average:</small>
                                <div>${sales.average.toLocaleString()}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' }}>
                        <div className="card-body text-white">
                            <h6 className="card-title opacity-75">📈 Week-over-Week Variation</h6>
                            <div className="mb-2">
                                <small className="opacity-75">Highest:</small>
                                <div className="fw-bold">{variation.highest.brand} — {variation.highest.value}%</div>
                            </div>
                            <div className="mb-2">
                                <small className="opacity-75">Lowest:</small>
                                <div>{variation.lowest.brand} — {variation.lowest.value}%</div>
                            </div>
                            <div>
                                <small className="opacity-75">Average:</small>
                                <div>{variation.average}%</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' }}>
                        <div className="card-body text-white">
                            <h6 className="card-title opacity-75">📦 Units Sold</h6>
                            <div className="mb-2">
                                <small className="opacity-75">Most:</small>
                                <div className="fw-bold">{units.most.brand} — {units.most.value.toLocaleString()}</div>
                            </div>
                            <div className="mb-2">
                                <small className="opacity-75">Least:</small>
                                <div>{units.least.brand} — {units.least.value.toLocaleString()}</div>
                            </div>
                            <div>
                                <small className="opacity-75">Average:</small>
                                <div>{units.average.toLocaleString()}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const overviewData = buildOverviewData();
    const breakdownData = buildBreakdownData();

    return (
        <Layout>
            <div className="container-fluid py-4">
                {/* Basket selector and management */}
                <div className="mb-3 d-flex align-items-center gap-3 flex-wrap">
                    <div className="dropdown">
                        <button className="btn btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                            <i className="bi bi-basket2 me-1"></i> Select basket
                        </button>
                        <ul className="dropdown-menu">
                            {baskets.map(b => (
                                <li key={b.id}>
                                    <button className="dropdown-item d-flex justify-content-between align-items-center"
                                        style={selectedBasket?.id === b.id ? {
                                            backgroundColor: '#eaf2ff',
                                            borderLeft: '3px solid #0d6efd',
                                            color: '#1f2937'
                                        } : undefined}
                                        onClick={() => handleSelectBasket(b)}>
                                        <span>{b.name} ({(() => {
                                            let asinsArr = [];
                                            if (Array.isArray(b.asins)) {
                                                asinsArr = b.asins;
                                            } else if (typeof b.asins === 'string') {
                                                try {
                                                    const arr = JSON.parse(b.asins);
                                                    if (Array.isArray(arr)) {
                                                        asinsArr = arr;
                                                    } else {
                                                        asinsArr = b.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                                                    }
                                                } catch {
                                                    asinsArr = b.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                                                }
                                            }
                                            // Merge ASIN\nBrand lines into ASIN,Brand
                                            const merged = [];
                                            for (let i = 0; i < asinsArr.length; i++) {
                                                const curr = asinsArr[i];
                                                if (typeof curr === 'object' && curr.asin) {
                                                    merged.push(curr);
                                                } else if (typeof curr === 'string') {
                                                    if (curr.includes(',')) {
                                                        merged.push(curr);
                                                    } else if (i + 1 < asinsArr.length && typeof asinsArr[i + 1] === 'string' && !asinsArr[i + 1].includes(',')) {
                                                        merged.push(curr + ',' + asinsArr[i + 1]);
                                                        i++;
                                                    } else {
                                                        merged.push(curr);
                                                    }
                                                }
                                            }
                                            return merged.length;
                                        })()} ASINs)</span>
                                        <span className="d-flex align-items-center gap-2 ms-3">
                                            <i className="bi bi-pencil-square text-primary" title="Edit basket" onClick={e => { e.stopPropagation(); openEdit(b); }}></i>
                                            <i className="bi bi-trash text-danger" onClick={e => { e.stopPropagation(); handleDelete(b.id); }}></i>
                                        </span>
                                    </button>
                                </li>
                            ))}
                            <li><hr className="dropdown-divider" /></li>
                            <li><button className="dropdown-item" onClick={() => setShowNew(true)}>
                                <i className="bi bi-plus-lg me-1"></i> New basket
                            </button></li>
                        </ul>
                    </div>
                    {selectedBasket && (
                        <span className="badge bg-primary px-3 py-2" style={{ fontSize: '0.9rem' }}>
                            Selected: <strong>{selectedBasket.name}</strong>
                        </span>
                    )}
                </div>
                {/* New Basket modal */}
                {showNew && (
                    <div className="card mb-4 border-primary">
                        <div className="card-body">
                            <h6>New Basket</h6>
                            <form onSubmit={handleCreate}>
                                <div className="row g-2">
                                    <div className="col-md-3">
                                        <input className="form-control form-control-sm" placeholder="Name" value={newName} onChange={e => setNewName(e.target.value)} required />
                                    </div>
                                    <div className="col-md-5">
                                        <textarea className="form-control form-control-sm" rows={2} placeholder="ASIN,Brand per line. Ex: B07PXGQC1Q,Coop Home Goods" value={newAsins} onChange={e => setNewAsins(e.target.value)} required />
                                    </div>
                                    <div className="col-md-2">
                                        <select className="form-select form-select-sm" value={newCountry} onChange={e => setNewCountry(e.target.value)}>
                                            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-2 d-flex gap-1">
                                        <button className="btn btn-primary btn-sm" type="submit">Create</button>
                                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setShowNew(false)}>Cancel</button>
                                    </div>
                                </div>
                            </form>
                            {error && <div className="alert alert-danger mt-2">{error}</div>}
                        </div>
                    </div>
                )}
                {/* Edit Basket modal */}
                {showEdit && (
                    <div className="card mb-4 border-info">
                        <div className="card-body">
                            <h6>Edit Basket</h6>
                            <form onSubmit={handleEdit}>
                                <div className="row g-2">
                                    <div className="col-md-3">
                                        <input className="form-control form-control-sm" placeholder="Name" value={editName} onChange={e => setEditName(e.target.value)} required />
                                    </div>
                                    <div className="col-md-5">
                                        <textarea className="form-control form-control-sm" rows={2} placeholder="ASIN,Brand per line. Ex: B07PXGQC1Q,Coop Home Goods" value={editAsins} onChange={e => setEditAsins(e.target.value)} required />
                                    </div>
                                    <div className="col-md-2">
                                        <select className="form-select form-select-sm" value={editCountry} onChange={e => setEditCountry(e.target.value)}>
                                            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-2 d-flex gap-1">
                                        <button className="btn btn-info btn-sm" type="submit">Save</button>
                                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => { setShowEdit(false); setEditing(null); }}>Cancel</button>
                                    </div>
                                </div>
                            </form>
                            {error && <div className="alert alert-danger mt-2">{error}</div>}
                        </div>
                    </div>
                )}
                <h2 className="mb-1">📊 Sales Estimate</h2>
                <p className="text-muted mb-3">
                    Weekly aggregates (Fri→Thu) from Jungle Scout. Upload a <code>.txt</code> or <code>.csv</code> file
                    with <strong>ASIN,Brand</strong> per line. The first line can be a header: <code>ASIN,Brand</code> (optional).
                </p>

                {/* Informational section — matching tracking2 */}
                <div className="alert alert-info border-0 shadow-sm mb-4" role="alert">
                    <h6 className="alert-heading fw-bold">ℹ️ How does it work</h6>
                    <ul className="mb-2 small">
                        <li>Upload a <code>.txt</code> or <code>.csv</code> file with ASINs (<strong>format: <code>ASIN,Brand</code> per line</strong>).</li>
                        <li>The first line can be a header: <code>ASIN,Brand</code> (optional).</li>
                        <li>Sample files are located in the <strong>tracking2</strong> project → folder <code>Asins/JS/</code>.</li>
                        <li>The system queries the <strong>Jungle Scout</strong> API and generates weekly aggregates (Friday → Thursday).</li>
                        <li>Each ASIN uses <strong>1 token</strong>. Monthly limit: <strong>1,000 tokens</strong>. Maximum <strong>10 ASINs</strong> per query.</li>
                        <li>Shows <strong>Highlights</strong> (sales, variation, units), <strong>Overview chart</strong> and <strong>Breakdown by Brand</strong>.</li>
                    </ul>
                    <small className="text-muted">Data comes from the <code>sales_estimates_query</code> endpoint of Jungle Scout.</small>
                </div>

                {/* Token usage badge */}
                {tokenInfo && (
                    <div className="mb-3">
                        <span className={`badge ${tokenBadgeClass()} me-2`} style={{ fontSize: '0.9rem' }}>
                            🪙 Tokens: {tokenInfo.used} / {tokenInfo.limit} used
                        </span>
                        <span className="text-muted small">({tokenInfo.remaining} remaining this month)</span>
                        {tokenInfo.used >= tokenInfo.limit * 0.9 && (
                            <div className="alert alert-danger mt-2 py-1 px-3 d-inline-block">
                                ⚠️ You are approaching the monthly token limit. Manage your queries.
                            </div>
                        )}
                    </div>
                )}

                {/* Input form — file upload */}
                <div className="card mb-4 border-0 shadow-sm">
                    <div className="card-body">
                        <h5 className="card-title">🔍 ASIN Query</h5>
                        <div className="row g-3 align-items-end">
                            <div className="col-md-5">
                                <label className="form-label fw-bold">ASINs File (.txt or .csv)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept=".txt,.csv"
                                    onChange={handleFileChange}
                                />
                                <small className="text-muted">
                                    Format: <code>ASIN,Brand</code> per line. Example: <code>B0CWVYBYBQ,EcoZoom</code>
                                </small>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-bold">Marketplace</label>
                                <select
                                    className="form-select"
                                    value={marketplace}
                                    onChange={e => setMarketplace(e.target.value)}
                                >
                                    <option value="us">🇺🇸 US</option>
                                    <option value="mx">🇲🇽 MX</option>
                                    <option value="ca">🇨🇦 CA</option>
                                    <option value="uk">🇬🇧 UK</option>
                                    <option value="de">🇩🇪 DE</option>
                                    <option value="fr">🇫🇷 FR</option>
                                    <option value="it">🇮🇹 IT</option>
                                    <option value="es">🇪🇸 ES</option>
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-bold">Metric</label>
                                <div className="btn-group w-100" role="group">
                                    <button className={`btn btn-sm ${metricMode === 'sales_amount' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setMetricMode('sales_amount')}
                                    >
                                        💰 Sales
                                    </button>
                                    <button className={`btn btn-sm ${metricMode === 'units_sold' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setMetricMode('units_sold')}
                                    >
                                        📦 Units
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <button
                                    className="btn btn-primary w-100"
                                    onClick={handleSubmit}
                                    disabled={loading || parsedAsins.length === 0}
                                >
                                    {loading ? (
                                        <><span className="spinner-border spinner-border-sm me-2" />Loading...</>
                                    ) : (
                                        <>🚀 Run</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Preview of parsed ASINs */}

                        {(parsedAsins.length > 0) && (
                            <div className="mt-3">
                                <span className="badge bg-primary me-2">{parsedAsins.length} ASINs detected</span>
                                <span className="text-muted small">
                                    🪙 This query will use <strong>{parsedAsins.length}</strong> token(s)
                                </span>
                                <div className="mt-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.8rem' }}>
                                        <thead className="table-light">
                                            <tr><th>#</th><th>ASIN</th><th>Brand</th></tr>
                                        </thead>
                                        <tbody>
                                            {parsedAsins.map((a, i) => (
                                                <tr key={i}>
                                                    <td>{i + 1}</td>
                                                    <td><code>{a.asin}</code></td>
                                                    <td>{a.brand}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {file && parsedAsins.length === 0 && (
                            <div className="alert alert-warning mt-3 mb-0 py-2">
                                ⚠️ No valid ASINs found in file. Check the format: <code>ASIN,Brand</code> per line.
                            </div>
                        )}
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="alert alert-danger" role="alert">
                        <strong>Error:</strong> {error}
                    </div>
                )}

                {/* Failures warning */}
                {safeResult.failures && safeResult.failures.length > 0 && (
                    <div className="alert alert-warning">
                        <strong>⚠️ Failed ASINs:</strong>
                        <ul className="mb-0 mt-1">
                            {safeResult.failures.map((f, i) => (
                                <li key={i}><code>{f.asin}</code>: {f.error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Results */}
                {safeResult.weekly.length > 0 || safeResult.highlights || safeResult.failures.length > 0 ? (
                    <>
                        {/* Date range info */}
                        <div className="mb-3 text-muted">
                            📅 Date Range: <strong>{safeResult.dateRange.startDate}</strong> → <strong>{safeResult.dateRange.endDate}</strong>
                            {' | '}🪙 Tokens used: <strong>{safeResult.tokensUsed}</strong> | Remaining: <strong>{safeResult.tokensRemaining}</strong>
                        </div>

                        {/* Highlights */}
                        <h4 className="mb-3">✨ Highlights (selected week)</h4>
                        {renderHighlights()}

                        {/* Overview graph */}
                        {overviewData && (
                            <div className="card mb-4 border-0 shadow-sm">
                                <div className="card-body">
                                    <Plot
                                        data={overviewData.traces}
                                        layout={overviewData.layout}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Breakdown by brand */}
                        {breakdownData && breakdownData.length > 0 && (
                            <div className="card mb-4 border-0 shadow-sm">
                                <div className="card-body">
                                    <h5>🔎 Breakdown by Brand</h5>
                                    <div className="row">
                                        {breakdownData.map((bd, i) => (
                                            <div className="col-md-4 mb-3" key={bd.brand}>
                                                <Plot
                                                    data={[bd.trace]}
                                                    layout={{
                                                        title: bd.brand,
                                                        height: 250,
                                                        margin: { l: 60, r: 10, t: 40, b: 55 },
                                                        showlegend: false,
                                                        xaxis: { type: 'date', tickformat: '%b %d', tickangle: 0 },
                                                        yaxis: { title: { text: metricMode === 'sales_amount' ? 'Sales (USD)' : 'Units Sold' } },
                                                    }}
                                                    config={{ responsive: true }}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Weekly data table (expandable like tracking2) */}
                        {safeResult.weekly.length > 0 && (
                            <div className="card mb-4 border-0 shadow-sm">
                                <div className="card-body">
                                    <details>
                                        <summary className="fw-bold mb-2" style={{ cursor: 'pointer' }}>
                                            📄 Show weekly table (with ASIN links)
                                        </summary>
                                        <div className="table-responsive mt-2">
                                            <table className="table table-hover table-sm">
                                                <thead className="table-dark">
                                                    <tr>
                                                        <th>Week End</th>
                                                        <th>Brand</th>
                                                        <th>ASIN</th>
                                                        <th className="text-end">Units Sold</th>
                                                        <th className="text-end">Sales ($)</th>
                                                        <th className="text-end">Avg Price ($)</th>
                                                        <th>Amazon</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {safeResult.weekly.map((row, i) => (
                                                        <tr key={i}>
                                                            <td>{row.week_end}</td>
                                                            <td><span className="badge bg-secondary">{row.brand}</span></td>
                                                            <td><code>{row.asin}</code></td>
                                                            <td className="text-end">{row.units_sold.toLocaleString()}</td>
                                                            <td className="text-end">${row.sales_amount.toLocaleString()}</td>
                                                            <td className="text-end">${row.avg_price.toFixed(2)}</td>
                                                            <td>
                                                                <a href={`https://www.${MARKETPLACE_DOMAINS[marketplace] || 'amazon.com'}/dp/${row.asin}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                                                    🔗
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </details>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        </Layout>
    );
}

export default SalesEstimate;